import { useDispatch } from 'react-redux';
import styles from '@/components/Modal/Modal.module.scss';
import Modal from '@/components/Modal/Modal';
import { Button } from '@/components/Button/Button';
import { fetchPost } from '@/hooks/useFetch';
import { useCurrentUserSelector } from '@/redux/selectors';
import {
  allAgeFocuses,
  allEthnicities,
  allGenders,
  allSexualities,
  valuesToOptions,
  Option,
  allFaiths,
  allLanguages,
} from '@/helpers';
import { FormInput } from '@/components/FormInput/FormInput';
import { useForm } from 'react-hook-form';
import Select from '@/components/Select/Select';
import { useState } from 'react';
import _ from 'lodash';

interface FocusesFormProps {
  buttonChildren?: any;
  buttonClassName?: string;
}

function FocusesForm(props: FocusesFormProps) {
  const { buttonChildren = <>Edit</>, buttonClassName = styles.link } = props;

  const userDoc = useCurrentUserSelector();

  const initalGender = valuesToOptions(userDoc?.gender) as Option[];
  const initalEthnicity = valuesToOptions(userDoc?.ethnicity) as Option[];
  const initialSexuality = valuesToOptions(userDoc?.sexuality) as Option[];
  const initialFaith = valuesToOptions(userDoc?.faith) as Option[];
  const initialLanguage = valuesToOptions(userDoc?.language) as Option[];

  const [gender, setGender] = useState(initalGender || []);
  const [ethnicity, setEthnicity] = useState(initalEthnicity || []);
  const [sexuality, setSexuality] = useState(initialSexuality || []);
  const [faith, setFaith] = useState(initialFaith || []);
  const [language, setLanguage] = useState(initialLanguage || []);

  const genderOnChange = (selection: Option[]) => {
    const values = _.map(selection, 'value');
    const isTrans = _.includes(values, 'Transgender');
    const validSelection = isTrans
      ? _.take(selection, 2)
      : _.take(selection, 1);

    setGender(validSelection);
  };

  const ethnicityOnChange = (selection: Option[]) => {
    setEthnicity(_.take(selection, 3));
  };

  const sexualityOnChange = (selection: Option[]) => {
    const values = _.map(selection, 'value');
    const isQueer = _.includes(values, 'LGBTQ+');
    const isStraight = _.includes(values, 'Straight / Heterosexual');

    let validSelection = isQueer ? _.take(selection, 3) : _.take(selection, 2);

    if (isStraight) {
      validSelection = _.filter(selection, {
        value: 'Straight / Heterosexual',
      });
    }

    setSexuality(validSelection);
  };

  const faithOnChange = (selection: Option[]) => {
    setFaith(_.take(selection, 2));
  };

  const languageOnChange = (selection: Option[]) => {
    setLanguage(_.take(selection, 4));
  };

  const form = useForm({
    defaultValues: {
      focuses_age: userDoc?.focuses_age,
    },
  });

  const dispatch = useDispatch();

  const createSaveChanges = (closeModal: Function) => {
    return (formData: any) => {
      fetchPost(
        '/api/user',
        {
          ...formData,
          gender: _.map(gender, 'value'),
          ethnicity: _.map(ethnicity, 'value'),
          sexuality: _.map(sexuality, 'value'),
          faith: _.map(faith, 'value'),
          language: _.map(language, 'value'),
        },
        { dispatch },
      );
      closeModal();
    };
  };

  return (
    <Modal
      buttonClassName={buttonClassName}
      buttonChildren={buttonChildren}
      render={({ closeModal }) => (
        <div className={styles.modalForm}>
          <div className={styles.modalHeader}>Identity & Client Focuses</div>
          <div>
            This is optional self reported information so we can better help
            clients that are looking to find providers with similar backgrounds.
          </div>

          <div className="form-field">
            <span className="form-label">Your Gender</span>
            <Select
              options={valuesToOptions(allGenders)}
              value={gender}
              onChange={genderOnChange}
              placeholder="Select your gender..."
              isClearable
              isMulti
            />
          </div>

          <div className="form-field">
            <span className="form-label">Your Ethnicity</span>
            <Select
              options={valuesToOptions(allEthnicities)}
              value={ethnicity}
              onChange={ethnicityOnChange}
              placeholder="Select your ethnicity..."
              isClearable
              isMulti
            />
          </div>

          <div className="form-field">
            <span className="form-label">Your Sexuality</span>
            <Select
              options={valuesToOptions(allSexualities)}
              value={sexuality}
              onChange={sexualityOnChange}
              placeholder="Select your sexuality..."
              isClearable
              isMulti
            />
          </div>

          <div className="form-field">
            <span className="form-label">Your Faith</span>
            <Select
              options={valuesToOptions(allFaiths)}
              value={faith}
              onChange={faithOnChange}
              placeholder="Select your faith..."
              isClearable
              isMulti
            />{' '}
          </div>

          <div className="form-field">
            <span className="form-label">
              Languages You Speak Other Than English
            </span>
            <Select
              options={valuesToOptions(allLanguages)}
              value={language}
              onChange={languageOnChange}
              placeholder="Select your languages..."
              isClearable
              isMulti
            />
          </div>

          <div className="form-field">
            <span className="form-label">Age Group Focuses</span>
            <FormInput
              type="toggle-group"
              form={form}
              name="focuses_age"
              options={allAgeFocuses}
            />
          </div>

          <div className="form-actions">
            <Button className="btn" color="grey" onClick={closeModal}>
              Cancel
            </Button>

            <Button
              className="btn"
              onClick={form.handleSubmit(createSaveChanges(closeModal))}
            >
              Save Changes
            </Button>
          </div>
        </div>
      )}
    />
  );
}

export default FocusesForm;
